import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { AML_SCREENING_STATUS } from '../dataHandlers/amlScreeningConstants';
import { useGetCustomerAmlScreeningHandler } from '../dataHandlers/getCustomerAmlScreeningHandler';

const TAB_DETAILS = 'details';
const TAB_RISK = 'risk';
const TAB_AML = 'aml';
export default function CustomerDetailsTabs({ customer, children }) {
  const dispatch = useDispatch();
  const { customers: c } = useTranslatedMessages();
  const tabs = c?.pages?.details?.tabs ?? {};
  const amlErrors = c?.amlScreening?.errors ?? {};
  const subscription = useSelector((state) => state.subscription.subscription);
  const [searchParams, setSearchParams] = useSearchParams();
  const credentialsSnackbarShownRef = useRef(false);

  const isBusinessCustomer = customer?.type === 'BUSINESS';
  const isRiskComplianceEnabled = subscription?.features?.enable_risk_compliance;
  const isAMLScreeningEnabled = subscription?.features?.enable_aml_screening;
  const profileId = customer?.profile_id;
  const shouldPrefetchAml = Boolean(profileId) && isBusinessCustomer && isAMLScreeningEnabled;
  const { amlScreeningData, isLoading: isAmlLoading } = useGetCustomerAmlScreeningHandler(profileId, shouldPrefetchAml);
  const isAmlCredentialsMissing = amlScreeningData?.status === AML_SCREENING_STATUS.CREDENTIALS_NOT_CONFIGURED;

  const tabFromUrl = searchParams.get('tab');
  const activeTab = useMemo(() => {
    if (tabFromUrl === TAB_RISK) {
      return TAB_RISK;
    }
    if (tabFromUrl === TAB_AML && !isAmlCredentialsMissing) {
      return TAB_AML;
    }
    return TAB_DETAILS;
  }, [tabFromUrl, isAmlCredentialsMissing]);

  const availableTabs = useMemo(() => {
    const items = [{ key: TAB_DETAILS, label: tabs.details ?? 'Details' }];
    if (isBusinessCustomer && isRiskComplianceEnabled) {
      items.push({ key: TAB_RISK, label: tabs.riskCompliance ?? 'Risk & Compliance' });
    }
    if (isBusinessCustomer && isAMLScreeningEnabled) {
      items.push({ key: TAB_AML, label: tabs.amlScreening ?? 'AML Screening' });
    }
    return items;
  }, [
    isBusinessCustomer,
    isRiskComplianceEnabled,
    isAMLScreeningEnabled,
    tabs.details,
    tabs.riskCompliance,
    tabs.amlScreening,
  ]);

  useEffect(() => {
    if (tabFromUrl === TAB_RISK && (!isBusinessCustomer || !isRiskComplianceEnabled)) {
      setSearchParams({ tab: TAB_DETAILS }, { replace: true });
      return;
    }
    if (tabFromUrl === TAB_AML && (!isBusinessCustomer || !isAMLScreeningEnabled)) {
      setSearchParams({ tab: TAB_DETAILS }, { replace: true });
    }
  }, [tabFromUrl, isBusinessCustomer, isRiskComplianceEnabled, isAMLScreeningEnabled, setSearchParams]);

  useEffect(() => {
    if (tabFromUrl !== TAB_AML || isAmlLoading || !isAmlCredentialsMissing) {
      credentialsSnackbarShownRef.current = false;
      return;
    }

    setSearchParams({ tab: TAB_DETAILS }, { replace: true });

    if (credentialsSnackbarShownRef.current) {
      return;
    }

    credentialsSnackbarShownRef.current = true;
    dispatch(
      showSnackbar({
        message:
          amlErrors.credentialsNotConfigured ??
          'AML screening is not available. Digiveri credentials are not configured.',
        severity: 'error',
      }),
    );
  }, [
    amlErrors.credentialsNotConfigured,
    dispatch,
    isAmlCredentialsMissing,
    isAmlLoading,
    setSearchParams,
    tabFromUrl,
  ]);

  const handleTabChange = (_event, newValue) => {
    if (newValue === TAB_AML) {
      if (isAmlLoading) {
        return;
      }
      if (isAmlCredentialsMissing) {
        dispatch(
          showSnackbar({
            message:
              amlErrors.credentialsNotConfigured ??
              'Digiveri screening credentials have not been configured for your organization.',
            severity: 'error',
          }),
        );
        return;
      }
    }

    setSearchParams({ tab: newValue });
  };

  if (availableTabs.length <= 1) {
    return <Box>{children[TAB_DETAILS]}</Box>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor: 'grey.200',
          borderRadius: 1.5,
          p: 0.25,
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="customer details tabs"
          sx={{
            minHeight: 'unset',
            '& .MuiTabs-scroller': { display: 'flex', alignItems: 'center' },
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: 0.25, alignItems: 'center' },
            '& .MuiTab-root': {
              minWidth: 'auto',
              height: 32,
              minHeight: 32,
              py: 0,
              px: 2,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: 1,
              border: '1px solid transparent',
            },
            '& .MuiTab-root.Mui-selected': {
              color: 'text.primary',
              bgcolor: 'background.paper',
              borderColor: 'divider',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
            },
          }}
        >
          {availableTabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      <Box>{children[activeTab]}</Box>
    </Box>
  );
}
